import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, RefreshCw, ExternalLink } from 'lucide-react';
import { TreasuryCheckActions } from './TreasuryCheckActions';
import { IssuedChecksList } from './IssuedChecksList';
import { ReopeningPicker } from './ReopeningPicker';
import { MainnetConfirmDialog } from './MainnetConfirmDialog';
import { StablecoinPanel } from './StablecoinPanel';
import { ChecksRegister } from './ChecksRegister';

const UpgradeVerify = ({ d, busy, needsUpgrade, onUpgrade, onVerify }) => {
  const u = d.upgrade;
  const v = d.verification;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="tc-upgrade-verify">
      <div className="text-sm text-slate-300 space-y-1">
        <p>Contract version: <span className="text-white" data-testid="tc-contract-version">v{d.onchain?.version}</span>{d.onchain?.check_count != null && !needsUpgrade && ` · ${d.onchain.check_count} checks issued`}</p>
        {u && <p className={u.status === 'failed' ? 'text-red-400' : 'text-slate-400'} data-testid="tc-upgrade-status">Upgrade: {u.step}{u.status ? ` (${u.status})` : ''}{u.error ? ` — ${u.error}` : ''}</p>}
        {d.retired?.length > 0 && <p className="text-xs text-slate-500">Retired: {d.retired.map(r => r.address.slice(0, 10) + '…').join(', ')}</p>}
        <p className="text-xs text-slate-400" data-testid="tc-verification-status">Etherscan: {v ? `${v.status} (${v.result || ''})` : 'not verified'}{!d.etherscan_key_set && ' · Etherscan key not added'}</p>
      </div>
      <div className="flex gap-2">
        {needsUpgrade && <button onClick={onUpgrade} disabled={busy} data-testid="tc-upgrade-btn" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg text-sm font-semibold disabled:opacity-40">Upgrade to latest version</button>}
        {!needsUpgrade && v?.status !== 'verified' && <button onClick={onVerify} disabled={busy || !d.etherscan_key_set} data-testid="tc-verify-btn" className={btn}>{v?.status === 'pending' ? 'Check verification' : 'Verify on Etherscan'}</button>}
      </div>
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check/admin`;
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const card = 'bg-slate-800 border border-slate-700 rounded-xl p-5';
const btn = 'px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-40';

const Kv = ({ k, v, testid }) => <p className="text-sm text-slate-400">{k}: <span className="text-white" data-testid={testid}>{v ?? '-'}</span></p>;

const Auction = ({ a, current, onSync, busy, deployed, token, onRegister }) => (
  <div className={card} data-testid="tc-latest-auction">
    <h3 className="text-white font-semibold mb-2">Treasury reopening reference</h3>
    <p className="text-xs text-slate-400 mb-2" data-testid="tc-active-series">Active on-chain: {current ? `#${current.id} ${current.cusip} · ${current.security_term} (${current.auction_date})` : 'none'}</p>
    {a ? (<>
      <p className="text-xs text-slate-500">Latest T-Bond reopening:</p>
      <Kv k="CUSIP" v={a.cusip} testid="tc-auction-cusip" /><Kv k="Term" v={a.security_term} /><Kv k="Auction date" v={a.auction_date} testid="tc-auction-date" />
      <Kv k="Coupon / high yield / price" v={`${a.coupon_pct}% / ${a.high_yield_pct}% / ${a.price_per_100}`} />
    </>) : <p className="text-sm text-red-400">Auction data unavailable</p>}
    <button onClick={onSync} disabled={busy || !deployed || !a} data-testid="tc-sync-series-btn" className={`${btn} mt-3`}>Use latest T-Bond reopening</button>
    {deployed && <ReopeningPicker token={token} busy={busy} disabled={!deployed} onRegister={onRegister} />}
    <p className="text-[11px] text-slate-500 mt-3">The active series updates automatically every Monday to the newest reopening of the same type.</p>
  </div>
);

const Collateral = ({ p, onchain, onSync, busy, deployed }) => (
  <div className={card} data-testid="tc-collateral">
    <h3 className="text-white font-semibold mb-2">Collateral</h3>
    <Kv k="Treasury Reserve supply" v={`${Number(p.reserve_beth).toLocaleString()} BETH = ${usd(p.reserve_usd)}`} testid="tc-reserve-usd" />
    <Kv k="Signed credit notes" v={`${p.notes.length} note${p.notes.length === 1 ? '' : 's'} = ${usd(p.receivables_usd)}`} testid="tc-receivables-usd" />
    <Kv k="Collateral cap (now)" v={usd(p.cap_usd)} testid="tc-cap-preview" />
    <Kv k="Cap on-chain" v={onchain ? `${usd(onchain.collateral_cap_usd)} · updated ${onchain.collateral_updated_at || 'never'}` : '-'} testid="tc-cap-onchain" />
    <p className="text-[11px] text-slate-500 mt-1 break-all">{p.valuation_note} Snapshot hash {p.hash}</p>
    <button onClick={onSync} disabled={busy || !deployed} data-testid="tc-sync-collateral-btn" className={`${btn} mt-3`}>Anchor collateral on-chain</button>
  </div>
);

const Pledges = ({ pledges, onRelease, busy, sym }) => (
  <div className={card} data-testid="tc-pledges">
    <h3 className="text-white font-semibold mb-2">Pledges</h3>
    {pledges.length ? pledges.map(p => (
      <div key={p.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-700" data-testid={`tc-pledge-${p.id}`}>
        <span className="text-slate-300">#{p.id} · {p.amount} {sym} · {p.reference} · <span className="font-mono text-xs">{p.pledgee.slice(0, 10)}…</span></span>
        {p.released ? <span className="text-xs text-slate-500">released</span>
          : <button onClick={() => onRelease(p.id)} disabled={busy} data-testid={`tc-release-${p.id}`} className="text-xs text-amber-300 underline">Release</button>}
      </div>
    )) : <p className="text-sm text-slate-500">No pledges</p>}
  </div>
);

const History = ({ actions }) => (
  <div className={`${card} overflow-x-auto`} data-testid="tc-history">
    <h3 className="text-white font-semibold mb-2">On-chain activity</h3>
    <table className="w-full text-sm"><tbody className="divide-y divide-slate-700">
      {actions.map(a => (
        <tr key={a.action_id} className="text-slate-300">
          <td className="py-1.5 pr-3 text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
          <td className="py-1.5 pr-3 capitalize">{a.action.replace('_', ' ')}</td>
          <td className="py-1.5 pr-3 text-xs">{['amount', 'price_usd', 'discount_pct', 'reference', 'cusip', 'cap_usd', 'pledge_id'].filter(k => a.params?.[k] != null).map(k => `${k}: ${a.params[k]}`).join(' · ')}</td>
          <td className={`py-1.5 pr-3 text-xs ${a.status === 'failed' ? 'text-red-400' : 'text-emerald-400'}`}>{a.status}</td>
          <td className="py-1.5"><a href={a.explorer_url} target="_blank" rel="noreferrer" className="text-emerald-400"><ExternalLink className="w-4 h-4" /></a></td>
        </tr>
      ))}
      {!actions.length && <tr><td className="py-3 text-slate-500">No activity yet</td></tr>}
    </tbody></table>
  </div>
);

const NET_LABELS = { sepolia: 'Sepolia (test)', mainnet: 'Ethereum mainnet', base: 'Base', arbitrum: 'Arbitrum' };

export const AdminTreasuryCheck = ({ token }) => {
  const [network, setNetwork] = useState('sepolia');
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pas-admin/issuance-network`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data.network) setNetwork(r.data.network); }).catch(() => {});
  }, [token]);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checks, setChecks] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };
  const netRef = React.useRef(network);
  const load = useCallback(async () => {
    netRef.current = network;
    try {
      const r = await axios.get(`${API}/status`, { params: { network }, headers: { Authorization: `Bearer ${token}` } });
      if (r.data.network === netRef.current) setD(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Treasury Check status unavailable'); }
  }, [network, token]);
  useEffect(() => { setD(null); load(); }, [load]);
  const post = async (path, body, done) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${path}`, { network, ...body }, { headers });
      toast.success(r.data.message); done?.(); await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Transaction failed'); }
    finally { setBusy(false); }
  };
  const deployed = !!d?.contract?.address;
  const sym = d?.onchain?.version >= 3 ? 'BETH' : 'BTCK';
  const realNet = network !== 'sepolia';
  const [confirmAction, setConfirmAction] = useState(null);
  const guarded = (path) => (realNet ? setConfirmAction(path) : post(path, {}));
  const needsUpgrade = !!d?.onchain?.needs_upgrade;
  const upgrading = d?.upgrade?.status === 'running';
  useEffect(() => {
    if (!upgrading) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [upgrading, load]);
  useEffect(() => {
    if (!d?.contract?.address) return;
    axios.get(`${API}/checks`, { params: { network }, headers: { Authorization: `Bearer ${token}` } }).then(r => setChecks(r.data.checks)).catch(() => setChecks([]));
  }, [d, network, token]);
  const removeTest = async (c) => {
    try {
      const r = await axios.delete(`${API}/checks/${c.check_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message); setChecks(list => list.filter(x => x.check_id !== c.check_id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <div className="space-y-6" data-testid="admin-treasury-check">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><Landmark className="w-6 h-6 text-emerald-400" /> BETH Treasury Check ({sym}){sym === 'BETH' ? ' · USD Stablecoin' : ''}</h2>
          <p className="text-xs text-slate-400">ERC-20 digital treasury check · $1 face per token · collateralized by the Treasury Reserve supply and signed subscriber notes · Treasury CUSIP is reference data only</p>
        </div>
        <div className="flex gap-2">
          {['sepolia', 'mainnet', 'base', 'arbitrum'].map(n => (
            <button key={n} onClick={() => setNetwork(n)} data-testid={`tc-network-${n}`}
              className={`px-3 py-2 rounded-lg text-sm ${network === n ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300'}`}>{NET_LABELS[n]}</button>
          ))}
          <button onClick={load} className={btn} data-testid="tc-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      {!d || d.network !== network ? <p className="text-slate-400">Loading...</p> : (<>
        {d.chain_error && <p className="text-sm text-red-400" data-testid="tc-chain-error">{d.chain_error}</p>}
        <div className={card} data-testid="tc-contract">
          {deployed ? (
            <div className="grid sm:grid-cols-4 gap-3">
              <Kv k="Contract" v={<a href={d.contract.explorer_url} target="_blank" rel="noreferrer" className="font-mono text-emerald-400 underline">{d.contract.address.slice(0, 12)}…</a>} testid="tc-contract-address" />
              <Kv k="Total supply" v={`${d.onchain.total_supply.toLocaleString()} ${sym}`} testid="tc-total-supply" />
              <Kv k="In treasury" v={`${d.onchain.treasury_balance.toLocaleString()} ${sym}`} testid="tc-treasury-balance" />
              <Kv k="Pledged" v={`${d.onchain.pledged_total.toLocaleString()} ${sym}`} testid="tc-pledged-total" />
            </div>
          ) : d.contract?.pending_tx ? <p className="text-amber-300 text-sm">Deployment pending… <a href={d.contract.explorer_url} target="_blank" rel="noreferrer" className="underline">view</a></p> : d.chain_error ? <p className="text-sm text-slate-400">Contract state unavailable; try refresh.</p> : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-300" data-testid="tc-not-deployed">Not deployed on {NET_LABELS[network]}. Master wallet ETH: {d.eth_balance.toFixed(4)}{network === 'mainnet' && d.eth_balance < d.mainnet_deploy_min_eth && <span className="text-amber-300"> · fund {d.master_wallet} with at least {d.mainnet_deploy_min_eth} ETH to deploy</span>}</p>
              <button onClick={() => guarded('deploy')} disabled={busy} data-testid="tc-deploy-btn" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:opacity-40">{busy ? 'Deploying...' : `Deploy on ${NET_LABELS[network]}`}</button>
            </div>
          )}
        </div>
        {deployed && <UpgradeVerify d={d} busy={busy || upgrading} needsUpgrade={needsUpgrade} onUpgrade={() => guarded('upgrade')} onVerify={() => post('verify', {})} />}
        {deployed && !needsUpgrade && <StablecoinPanel network={network} token={token} onChanged={load} />}
        <MainnetConfirmDialog
          open={!!confirmAction}
          contractType="treasury_check"
          network={{ id: network, label: NET_LABELS[network] }}
          confirmText={`DEPLOY TO ${network.toUpperCase()}`}
          deployer={d?.master_wallet}
          token={token}
          onCancel={() => setConfirmAction(null)}
          onConfirm={(text) => { const path = confirmAction; setConfirmAction(null); post(path, { confirm_text: text }); }}
        />
        <div className="grid lg:grid-cols-2 gap-6">
          <Auction a={d.latest_auction} current={d.onchain?.current_series} onSync={() => post('sync-series', {})} busy={busy || needsUpgrade} deployed={deployed}
            token={token} onRegister={(sel) => post('sync-series', sel)} />
          <Collateral p={d.collateral_preview} onchain={d.onchain} onSync={() => post('sync-collateral', {})} busy={busy || needsUpgrade} deployed={deployed} />
        </div>
        {deployed && !needsUpgrade && <TreasuryCheckActions busy={busy} onSubmit={(kind, body, done) => post(kind, body, done)} />}
        {deployed && !needsUpgrade && <IssuedChecksList checks={checks} printPath={(id) => `/pas/admin/treasury-check/${id}`} onDelete={removeTest} />}
        <ChecksRegister token={token} />
        {deployed && <Pledges sym={sym} pledges={d.onchain.pledges} busy={busy} onRelease={(id) => post('release', { pledge_id: id })} />}
        <History actions={d.actions} />
      </>)}
    </div>
  );
};
